<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import {
  buildWorkflowEventSelectOptions,
  getWorkflowEventVisual,
} from './workflowEventTheme';

const props = defineProps({
  modelValue: { type: String, default: '' },
  events: { type: Array, required: true },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();
const open = ref(false);
const EVENT_ICON_SIZE = 20;

const options = computed(() => buildWorkflowEventSelectOptions(props.events));

const selectedOption = computed(() =>
  options.value.find(option => option.key === props.modelValue)
);

const selectedVisual = computed(() => getWorkflowEventVisual(props.modelValue));

const selectedLabel = computed(
  () => selectedOption.value?.label || props.modelValue
);

const close = () => {
  open.value = false;
};

const toggle = () => {
  open.value = !open.value;
};

const selectEvent = key => {
  emit('update:modelValue', key);
  emit('change');
  close();
};
</script>

<template>
  <div v-on-click-outside="close" class="relative w-full">
    <button
      type="button"
      class="flex w-full items-center gap-2.5 rounded-lg border border-n-weak bg-n-slate-2/70 px-3 py-2.5 text-start transition-colors dark:border-n-strong dark:bg-n-slate-3/30"
      :class="
        hasError
          ? '!border-n-ruby-8 dark:!border-n-ruby-8'
          : open
            ? 'border-n-slate-7 dark:border-n-slate-7'
            : 'hover:border-n-slate-7 dark:hover:border-n-slate-7'
      "
      :aria-label="t('AUTOMATION.ADD.FORM.EVENT.LABEL')"
      :aria-expanded="open"
      @click="toggle"
    >
      <HugeIcon
        :icon="selectedVisual.hugeIcon"
        :size="EVENT_ICON_SIZE"
        class="pointer-events-none shrink-0"
        :class="selectedVisual.iconClass"
      />
      <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
        {{ selectedLabel }}
      </span>
      <span
        class="inline-flex h-5 w-5 min-h-5 min-w-5 shrink-0 items-center justify-center text-[20px] leading-none text-n-slate-11"
        :class="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="open"
      class="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-lg dark:border-n-strong dark:bg-n-solid-2"
      role="listbox"
    >
      <ul class="m-0 max-h-80 list-none overflow-y-auto p-1">
        <li v-for="option in options" :key="option.key" role="none">
          <button
            type="button"
            role="option"
            class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-start transition-colors"
            :class="
              option.key === modelValue
                ? 'bg-n-blue-3 dark:bg-n-blue-4/40'
                : 'hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3'
            "
            :aria-selected="option.key === modelValue"
            @click="selectEvent(option.key)"
          >
            <HugeIcon
              :icon="option.icon"
              :size="EVENT_ICON_SIZE"
              class="pointer-events-none shrink-0"
              :class="option.iconClass"
            />
            <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
              {{ option.label }}
            </span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
